import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ToastService } from 'src/app/services/toast.service';
import { ListingService } from 'src/app/services/marketplace/listing.service';
import { CartService } from 'src/app/services/marketplace/cart.service';
import { FavoriteService } from 'src/app/services/marketplace/favorite.service';
import { ReviewService } from 'src/app/services/marketplace/review.service';
import { REPORT_REASONS, ReportReason, ReportService } from 'src/app/services/marketplace/report.service';
import { Favorite, Listing, Review } from 'src/app/services/marketplace/marketplace.models';
import {
  biomeRanges,
  elevationRanges,
  geozoneRanges,
  ruralLocationRanges,
  shopsRanges,
  urbanLocationRanges,
} from 'src/app/constant/constant';
import { AuthTokenService } from 'src/app/services/auth-token.service';

type Star = 'full' | 'half' | 'empty';

@Component({
  selector: 'app-listing-detail',
  templateUrl: './listing-detail.component.html',
  styleUrls: ['./listing-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ListingDetailComponent implements OnInit, OnDestroy {
  listing: Listing | null = null;
  reviews: Review[] = [];
  loading = true;
  addingToCart = false;
  /** Shows the "View cart" link once something was added from this page. */
  addedToCart = false;
  claiming = false;
  quantity = 1;

  favoriteId: number | null = null;

  /** Gallery: which picture is large, and whether the full-screen viewer is open. */
  activeImage = 0;
  viewerOpen = false;

  /** "Report this item" form. */
  reportOpen = false;
  reportReason: ReportReason | null = null;
  reportDetails = '';
  reporting = false;
  /** Shown after sending, until the form is closed. */
  reportSent = false;
  readonly reportReasons = REPORT_REASONS;

  /** "More from this shop" (up to 4, not this item). */
  moreFromShop: Listing[] = [];

  draftRating = 0;
  draftBody = '';
  submittingReview = false;
  readonly starChoices = [1, 2, 3, 4, 5];

  private subs = new Subscription();
  private loadSeq = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private listingService: ListingService,
    private cartService: CartService,
    private favoriteService: FavoriteService,
    private reviewService: ReviewService,
    private reportService: ReportService,
    private authTokens: AuthTokenService,
    private toaster: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // paramMap, not a snapshot: "More from this shop" links reuse this page.
    this.subs.add(
      this.route.paramMap.subscribe((params) => {
        const id = params.get('id');
        if (id) {
          this.load(id);
        } else {
          this.loading = false;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.viewerOpen) this.closeViewer();
    if (this.reportOpen) this.closeReport();
  }

  private load(id: string): void {
    const seq = ++this.loadSeq; // ignore answers for an item we already left
    this.loading = true;
    this.listing = null;
    this.reviews = [];
    this.moreFromShop = [];
    this.activeImage = 0;
    this.addedToCart = false;
    this.quantity = 1;
    this.draftRating = 0;
    this.draftBody = '';
    this.cdr.detectChanges();

    this.listingService.get(id).subscribe({
      next: (listing) => {
        if (seq !== this.loadSeq) return;
        this.listing = listing;
        this.loading = false;
        this.cdr.detectChanges();
        this.loadMoreFromShop(listing, seq);
        // "Write a review" on My Purchases links here with #reviews.
        if (this.route.snapshot.fragment === 'reviews') {
          setTimeout(() => document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        } else {
          window.scrollTo({ top: 0 });
        }
      },
      error: () => {
        if (seq !== this.loadSeq) return;
        this.loading = false;
        this.cdr.detectChanges();
      },
    });

    this.reviewService
      .listForListing(id)
      .pipe(catchError(() => of([] as Review[])))
      .subscribe((reviews) => {
        if (seq !== this.loadSeq) return;
        this.reviews = reviews;
        this.cdr.detectChanges();
      });

    // Optional on this public page — signed-out visitors just get no heart.
    this.favoriteService
      .list()
      .pipe(catchError(() => of([] as Favorite[])))
      .subscribe((favs) => {
        if (seq !== this.loadSeq) return;
        const match = favs.find((f) => String(f.listing) === id);
        this.favoriteId = match?.id ?? null;
        this.cdr.detectChanges();
      });
  }

  private loadMoreFromShop(listing: Listing, seq: number): void {
    this.listingService
      .search({ listing_type: 'one_time', vendor: listing.vendor.shop_slug, sort: 'newest' })
      .pipe(catchError(() => of(null)))
      .subscribe((page) => {
        if (seq !== this.loadSeq || !page) return;
        this.moreFromShop = page.items.filter((item) => item.id !== listing.id).slice(0, 4);
        this.cdr.detectChanges();
      });
  }

  // ---- gallery ------------------------------------------------------------

  get images(): string[] {
    const gallery = this.listing?.gallery?.map((picture) => picture.url) ?? [];
    if (gallery.length) return gallery;
    return this.listing?.cover_image ? [this.listing.cover_image] : [];
  }

  showImage(index: number): void {
    const count = this.images.length;
    if (!count) return;
    this.activeImage = (index + count) % count; // wraps around at both ends
  }

  openViewer(index = this.activeImage): void {
    if (!this.images.length) return;
    this.activeImage = index;
    this.viewerOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeViewer(): void {
    this.viewerOpen = false;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.reportOpen && event.key === 'Escape') {
      this.closeReport();
      return;
    }
    if (!this.viewerOpen) return;
    if (event.key === 'Escape') this.closeViewer();
    else if (event.key === 'ArrowRight') this.showImage(this.activeImage + 1);
    else if (event.key === 'ArrowLeft') this.showImage(this.activeImage - 1);
  }

  // ---- report this item ---------------------------------------------------

  openReport(): void {
    if (!this.authTokens.hasSession) {
      this.toaster.showNote('Please sign in to report an item.', 'warning');
      return;
    }
    this.reportOpen = true;
    this.reportSent = false;
    this.reportReason = null;
    this.reportDetails = '';
    document.body.style.overflow = 'hidden';
  }

  closeReport(): void {
    this.reportOpen = false;
    document.body.style.overflow = '';
  }

  get reportReady(): boolean {
    // "Something else" needs a few words to be useful to staff.
    return !!this.reportReason && (this.reportReason !== 'other' || this.reportDetails.trim().length >= 5);
  }

  submitReport(): void {
    if (!this.listing || !this.reportReason || this.reporting || !this.reportReady) return;
    this.reporting = true;
    this.reportService.reportListing(this.listing.id, this.reportReason, this.reportDetails.trim()).subscribe({
      next: () => {
        this.reporting = false;
        this.reportSent = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.reporting = false;
        if (err?.status !== 401) {
          // 401 already showed "please sign in" centrally.
          const detail = err?.error?.non_field_errors?.[0] || err?.error?.details?.[0] || err?.error?.detail;
          this.toaster.showNote(detail || 'Could not send your report. Please try again.', 'warning');
        }
        this.cdr.detectChanges();
      },
    });
  }

  // ---- what's included ----------------------------------------------------

  /** "2 PDFs", "3 images"… from the listing's downloadable files. */
  get includedFiles(): { kind: 'doc' | 'image' | 'other'; label: string }[] {
    const files = this.listing?.files ?? [];
    const count = { doc: 0, image: 0, other: 0 };
    files.forEach((file) => {
      const name = (file.name || '').toLowerCase();
      if (file.format === 'image' || /\.(png|jpe?g|webp|gif|svg)$/.test(name)) count.image++;
      else if (file.format === 'pdf' || /\.(pdf|docx?|txt|md|rtf)$/.test(name)) count.doc++;
      else count.other++;
    });
    const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
    const rows: { kind: 'doc' | 'image' | 'other'; label: string }[] = [];
    if (count.image) rows.push({ kind: 'image', label: plural(count.image, 'map / image', 'maps / images') });
    if (count.doc) rows.push({ kind: 'doc', label: plural(count.doc, 'document', 'documents') });
    if (count.other) rows.push({ kind: 'other', label: plural(count.other, 'other file', 'other files') });
    return rows;
  }

  get licenseLabel(): string {
    const value = (this.listing?.attributes as Record<string, unknown> | undefined)?.['license_type'];
    return value ? String(value) : '';
  }

  // ---- ratings ------------------------------------------------------------

  /** Five stars for a 0–5 rating, in half steps. */
  stars(rating: number | string | null | undefined): Star[] {
    const value = Math.round((Number(rating) || 0) * 2) / 2;
    return [1, 2, 3, 4, 5].map((n) => (value >= n ? 'full' : value >= n - 0.5 ? 'half' : 'empty'));
  }

  get ratingAverage(): number {
    return Number(this.listing?.rating_avg) || 0;
  }

  setRating(rating: number): void {
    this.draftRating = rating;
  }

  submitReview(): void {
    if (!this.listing || this.submittingReview || this.draftRating < 1) {
      return;
    }
    this.submittingReview = true;

    this.reviewService
      .create({ listing: this.listing.id, rating: this.draftRating, body: this.draftBody.trim() })
      .subscribe({
        next: (review) => {
          // Preview fixtures answer with an empty array rather than a Review,
          // so only prepend when a real record comes back.
          if (review?.id) {
            this.reviews = [review, ...this.reviews];
          }
          if (this.listing) {
            this.listing.can_review = false; // one review per purchase
            // The server recounts the average; mirror it so the page updates now.
            const count = (this.listing.rating_count || 0) + 1;
            this.listing.rating_avg =
              ((Number(this.listing.rating_avg) || 0) * (count - 1) + this.draftRating) / count;
            this.listing.rating_count = count;
          }
          this.draftRating = 0;
          this.draftBody = '';
          this.submittingReview = false;
          this.toaster.showSuccess('Thanks for your review!', 'success');
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.submittingReview = false;
          // The API rejects reviews from people who haven't bought the listing,
          // and duplicates from the same buyer.
          const detail = err?.error?.non_field_errors?.[0] || err?.error?.detail || err?.error?.[0];
          this.toaster.showNote(
            detail || 'You can review this once you have purchased it.',
            'warning',
          );
          this.cdr.detectChanges();
        },
      });
  }

  // ---- favourite / buy ----------------------------------------------------

  get isFavorited(): boolean {
    return this.favoriteId != null;
  }

  toggleFavorite(): void {
    if (!this.listing) return;

    if (this.favoriteId != null) {
      const id = this.favoriteId;
      this.favoriteId = null; // optimistic
      this.favoriteService.remove(id).subscribe({ error: () => (this.favoriteId = id) });
      return;
    }

    const optimisticId = -Date.now();
    this.favoriteId = optimisticId; // optimistic
    this.favoriteService.add(this.listing.id).subscribe({
      next: (fav) => { if (fav?.id) this.favoriteId = fav.id; },
      error: () => (this.favoriteId = null),
    });
  }

  addToCart(): void {
    if (!this.listing) return;
    this.addingToCart = true;
    this.cartService.addLine(this.listing.id, this.quantity).subscribe({
      next: () => {
        this.addingToCart = false;
        this.addedToCart = true;
        this.toaster.showSuccess('Added to cart.', 'success');
        this.cdr.detectChanges();
      },
      error: () => {
        this.addingToCart = false;
        this.toaster.showNote('Could not add this to your cart.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * The product's game details (from listing.attributes, filled from its
   * Content Vault item), in the order the WordPress product page used.
   * Only rows with a value; empty for listings with no game details.
   */
  get specRows(): { label: string; value: string }[] {
    const a = (this.listing?.attributes || {}) as Record<string, any>;
    const list = (v: unknown): string => (Array.isArray(v) ? v.filter(Boolean).join(', ') : '');
    // "Any" when every option of a filter group is ticked (the same option
    // lists the Content Vault form offers) - nine elevations in a row say less.
    const anyOf = (v: unknown, options: string[]): string => {
      if (!Array.isArray(v) || !v.length) return '';
      const picked = new Set(v.map((item) => String(item).trim().toLowerCase()));
      return options.every((option) => picked.has(option.trim().toLowerCase())) ? 'Any' : list(v);
    };
    const text = (v: unknown): string => (v == null ? '' : String(v).trim());
    const num = (v: unknown): number => Number(v) || 0;

    const partyMin = num(a['party_min_size']);
    const partyMax = num(a['party_max_size']);
    const party =
      partyMin && partyMax ? `${partyMin}–${partyMax}` : partyMin ? `${partyMin}+` : partyMax ? `up to ${partyMax}` : '';
    const aiUsage = (used: unknown, pct: unknown): string => {
      if (used == null) return '';
      if (!used) return 'No';
      return num(pct) ? `Yes (${Math.round(num(pct))}%)` : 'Yes';
    };

    const rows: [string, string][] = [
      ['Game genre', list(a['game_genre'])],
      ['Game system', list(a['gamesystem'])],
      ['Content type', list(a['type'])],
      ['Campaign', text(a['campaign_name'])],
      ['Playtime', num(a['playtime']) ? `${num(a['playtime'])} hours` : ''],
      ['Party size', party],
      ['Average party level', num(a['partyaverage']) ? String(num(a['partyaverage'])) : ''],
      ['Languages', list(a['languages'])],
      ['Content rating', text(a['content_warning'])],
      ['Digital readiness', text(a['digital_readiness_level'])],
      ['VTT compatible', list(a['virtual_table_top_selected'])],
      ['Platform add-ons', list(a['platform_specific_addons'])],
      ['Elevation', anyOf(a['elevation'], elevationRanges)],
      ['Geozone', anyOf(a['geozone'], geozoneRanges)],
      ['Biome', anyOf(a['biome'], biomeRanges)],
      ['Rural locations', anyOf(a['ruralLocation'], ruralLocationRanges)],
      ['Urban locations', anyOf(a['urbanLocation'], urbanLocationRanges)],
      ['Shops', anyOf(a['shops'], shopsRanges)],
      ['Themes', list(a['theme_tags'])],
      ['Primary antagonist', list(a['primary_antagonist'])],
      ['License', text(a['license_type'])],
      ['AI text generator used', aiUsage(a['ai_text_generator_used'], a['ai_text_tool_usage_percentage'])],
      ['AI image generator used', aiUsage(a['ai_image_generator_used'], a['ai_image_tool_usage_percentage'])],
    ];
    return rows.filter(([, value]) => value).map(([label, value]) => ({ label, value }));
  }

  get isFree(): boolean {
    return !!this.listing && Number(this.listing.price) === 0;
  }

  /** "$10.00" or "Free". */
  price(listing: Listing): string {
    const value = Number(listing.price);
    return value ? `$${value.toFixed(2)}` : 'Free';
  }

  /** Free listings skip the cart: claim them straight into My purchases / the Content Vault. */
  claimFree(): void {
    if (!this.listing || this.claiming) return;
    this.claiming = true;
    this.listingService.claim(this.listing.id).subscribe({
      next: (res) => {
        this.claiming = false;
        this.toaster.showSuccess(
          res.already_owned ? 'You already have this — opening your purchases.' : 'Added to your purchases and Content Vault.',
          'success',
        );
        this.router.navigate(['/marketplace/orders']);
      },
      error: (err) => {
        this.claiming = false;
        this.toaster.showNote(err?.error?.detail || err?.error?.[0] || 'Could not get this item.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  goToCart(): void {
    this.router.navigate(['/marketplace/cart']);
  }
}
